import { AddCircleOutline, ContentCopyOutlined, DeleteOutline, EditOutlined, MoreVert, RestoreOutlined, SaveAsOutlined, SaveOutlined } from "@mui/icons-material";
import { Tab, IconButton, Tabs, Typography, TextField, InputAdornment, Stack, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ActivityFilter } from "./filterSelector";
import React, { useEffect, useState } from "react";

export interface FilterTabProps {
    hasUnsavedChanges: boolean
    selectedFilter: ActivityFilter | null
    filters: ActivityFilter[]
    onDuplicate: () => void
    onSave: () => void
    onRemove: () => void
    onRename: (name: string) => void
    onRestore: () => void
    onSelectFilter: (selected: ActivityFilter | null) => void
    onCreateFilter: () => void
    //canEditSaved is false for a role that can create a saved filter but not
    //change or delete one. A filter that is not saved yet stays editable
    canEditSaved: boolean
}
export function FilterTab(props: FilterTabProps) {
    const selectedId = props.selectedFilter ? props.selectedFilter.id : "main";

    return <Tabs
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ sx: { display: "none" }}}
        sx={{ marginBottom: "20px", marginTop: "10px", borderBottom: (theme) => `1px solid ${theme.palette.custom.borderMuted}` }}
        value={selectedId}
        onChange={(e, value) => {

            if (value === "main") {
                props.onSelectFilter(null);
                return;
            }

            const filter = props.filters.find(x => x.id === value);

            if (filter) {
                props.onSelectFilter(filter);
            }
        }}
    >
        <Tab
            disableFocusRipple
            sx={{
                overflow: "visible",
                border: (theme) => `1px solid ${theme.palette.custom.borderMuted}`,
                borderBottom: "0",
                borderRadius: "10px 10px 0 0!important",
                marginRight: "5px",
            }}
            value={"main"}
            label={<FilterLabel
                active={props.selectedFilter === null}
                hasUnsavedChanges={false}
                selectedFilter={props.selectedFilter}
                filter={{ id: "main", name: "Main" } as any}
                onRestore={props.onRestore}
                onDuplicate={props.onDuplicate}
            />}
        />
        {
            props.filters?.filter(x => x.enabled).map(x => {
                const active = props.selectedFilter?.id === x.id;
                // NS-753
                const editable = props.canEditSaved || x.id.indexOf("temp") >= 0;

                return <Tab
                    disableFocusRipple
                    sx={{
                        overflow: "visible",
                        border: (theme) => `1px solid ${theme.palette.custom.borderMuted}`,
                        borderBottom: "0",
                        marginRight: "5px",
                        borderRadius: "10px 10px 0 0!important",
                        borderColor: (theme) => active && props.hasUnsavedChanges ? "red" : theme.palette.custom.borderMuted,
                    }} key={x.id} value={x.id} label={<FilterLabel
                        active={active}
                        hasUnsavedChanges={active && props.hasUnsavedChanges}
                        selectedFilter={props.selectedFilter}
                        filter={x}
                        onRestore={props.onRestore}
                        onSave={editable ? props.onSave : undefined}
                        onDuplicate={props.onDuplicate}
                        onRemove={editable ? props.onRemove : undefined}
                        onRename={editable ? props.onRename : undefined} />}
                />;
            })
        }
        <IconButton disabled={Boolean(props.selectedFilter && props.selectedFilter.id.indexOf("temp") >= 0)} sx={{ alignSelf: "start" }} onClick={(() => props.onCreateFilter())} size="small" color="primary"><AddCircleOutline /></IconButton>
    </Tabs>;
}


interface FilterLabelProps {
    active: boolean
    hasUnsavedChanges: boolean
    selectedFilter: ActivityFilter | null
    filter: ActivityFilter
    onRestore: () => void
    onSave?: () => void
    onRename?: (name: string) => void
    onDuplicate: () => void
    onRemove?: () => void
}

function FilterLabel(props: FilterLabelProps) {

    const [isRenaming, setIsRenaming] = useState(false);
    const [newName, setNewName] = useState("");
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleteDialogText, setDeleteDialogText] = useState("");

    const options = [];

    if (props.onRename) {
        options.push({
            id: "rename",
            text: "Rename",
            icon: <EditOutlined />,
        });
    }


    if (props.filter.id.indexOf("temp") === -1 && props.active && props.hasUnsavedChanges) {
        options.push({
            id: "restore",
            text: "Restore",
            icon: <RestoreOutlined />,
        });
    }

    if (props.onSave) {
        options.push({
            id: "save",
            text: "Save",
            icon: <SaveOutlined />,
        });
    }

    if (props.filter.id.indexOf("temp") === -1) {
        if (props.filter.id === "main" || (props.active && props.hasUnsavedChanges)) {
            options.push({
                id: "duplicate",
                text: "Save As...",
                icon: <SaveAsOutlined />,
            });
        } else {
            options.push({
                id: "duplicate",
                text: "Duplicate",
                icon: <ContentCopyOutlined />,
            });
        }
    }

    if (props.onRemove) {
        options.push({
            id: "remove",
            text: "Remove",
            icon: <DeleteOutline />,
        });
    }

    const onSelected = (id: string) => {
        if (id === "save" && props.onSave) {
            props.onSave();
            return;
        }

        if (id === "rename") {
            setNewName(props.filter.name);
            setIsRenaming(true);
            return;
        }

        if (id === "duplicate") {
            props.onDuplicate();
            return;
        }

        if (id === "restore") {
            props.onRestore();
            return;
        }

        if (id === "remove") {
            setDeleteDialogText(`Are you sure you want to delete the filter "${props.filter.name}" ?`);
            setDeleteDialog(true);
            return;
        }
    };

    const save = () => {
        setIsRenaming(false);

        if (props.onRename) {
            props.onRename(newName);
        }
    };

    useEffect(() => {
        setIsRenaming(false);
    }, [props.selectedFilter]);

    return <Stack direction="row" gap={1}>
        {isRenaming ?
            <TextField
                autoFocus
                label="Filter Name"
                size="small"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                InputProps={{
                    endAdornment: <InputAdornment position="end">
                        <IconButton
                            size="small"
                            onClick={save}
                            edge="end"
                        >
                            <SaveOutlined />
                        </IconButton>
                    </InputAdornment>
                }}
            />
            :
            <Stack direction="row" gap={1} alignItems={"center"}>
                <FilterItemMenu visible={props.active && !isRenaming} options={options} onSelected={onSelected} />
                <Typography
                    sx={{
                        textWrap: "nowrap"
                    }}
                    title={props.filter.name}
                    minWidth={"40px"}
                    maxWidth={"250px"}
                    overflow={"hidden"}
                    textOverflow={"ellipsis"}
                    fontStyle={props.hasUnsavedChanges ? "italic" : "inherit"}
                    color={props.hasUnsavedChanges ? "error" : "primary"}
                    marginLeft={props.active && !isRenaming ? "15px" : "auto"}>{props.filter.name}</Typography>
            </Stack>
        }

        <ConfirmDialog
            open={deleteDialog}
            title="Delete Filter"
            message={deleteDialogText}
            onConfirm={() => props.onRemove && props.onRemove()}
            onCancel={() => setDeleteDialog(false)}
        />
    </Stack>;
}

interface FilterItemMenuProps {
    visible: Boolean
    options: FilterItemMenuOption[]
    onSelected?: (selected: string) => void
}

interface FilterItemMenuOption {
    id: string
    icon?: React.ReactChild
    text: string
}

function FilterItemMenu(props: FilterItemMenuProps) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
            >
                {props.options.map((x, index) =>
                    <MenuItem key={index} onClick={() => {
                        setAnchorEl(null);
                        props.onSelected && props.onSelected(x.id);
                    }}>
                        {x.icon ? <ListItemIcon>{x.icon}</ListItemIcon> : null}
                        <ListItemText>{x.text}</ListItemText>
                    </MenuItem>
                )}
            </Menu>
            {props.visible && <IconButton
                sx={{
                    width: "20px",
                    marginLeft: "-17px",
                    marginTop: "0px",
                    padding: "0",
                    height: "50px",
                    borderRadius: "0",
                    borderTopLeftRadius: "10px",
                    position: "absolute",
                    color: "white",
                    backgroundColor: "primary.main",
                    "&:hover": { backgroundColor: "primary.main" }

                }}
                size="small"
                onClick={handleClick}>
                <MoreVert />
            </IconButton >}

        </>);
}